import {
    ChangeDetectionStrategy,
    Component,
    effect,
    inject,
    input,
    InputSignal,
    signal,
    WritableSignal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';

import {
    ButtonComponent,
    ButtonType,
    IconPosition,
} from '@connect-services/src/lib/components/button/button.component';
import { ProjectControllerGet200Response, ProjectsService } from '@connect-services/src/sdk/api';
import { Project } from '@connect-services/src/sdk/api/model/project';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters, OrderEnum, PaginationParameters } from '@shared/client/models/pagination.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, RessourcesModule],
    selector: 'app-user-detail-project-list',
    templateUrl: './user-detail-project-list.component.html',
})
export class UserDetailProjectListComponent {
    public minDisplay = 5;
    public readonly personId: InputSignal<string> = input.required<string>();
    public readonly previousUserId: WritableSignal<string | undefined> = signal(undefined);
    public readonly projects: WritableSignal<Project[] | null> = signal<Project[] | null>(null);
    public readonly showAll: WritableSignal<boolean> = signal(false);
    public readonly totalProjectsCount: WritableSignal<number> = signal(0);

    protected readonly ButtonType = ButtonType;
    protected readonly IconPosition = IconPosition;

    private projectsService = inject(ProjectsService);

    public constructor() {
        effect(async () => {
            if (this.previousUserId() === this.personId()) {
                return;
            }
            this.totalProjectsCount.set(0);
            await this.fetchProjects();
            this.previousUserId.set(this.personId());
        });
    }

    public async toggleShow(): Promise<void> {
        this.showAll.set(!this.showAll());
        await this.fetchProjects();
    }

    private async fetchProjects(): Promise<void> {
        if (this.totalProjectsCount() !== 0 && this.projects()?.length === this.totalProjectsCount()) {
            return;
        }

        let totalItems = 0;

        let hasNextPage = false;
        const paginationParameters: PaginationParameters = {
            ...defaultPaginationParameters,
            order: OrderEnum.ASC,
            orderBy: 'name',
            take: this.minDisplay,
        };

        const projects: Project[] = [];

        do {
            const paginatedProjects: ProjectControllerGet200Response = await firstValueFrom(
                this.projectsService.projectControllerGet({
                    ...paginationParameters,
                    person: this.personId(),
                }),
            );

            projects.push(...paginatedProjects.items);

            if (totalItems === 0) {
                totalItems = paginatedProjects.totalItems;
            }

            hasNextPage = paginatedProjects.hasNextPage;
            paginationParameters.page += 1;
        } while (hasNextPage && this.showAll());

        this.projects.set(projects);

        this.totalProjectsCount.set(totalItems);
    }
}
